import apiClient from './api';
import { LoginCredentials, LoginResponse, User, ApiResponse } from '@/types/auth';
import { TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY, USER_STORAGE_KEY, LAST_ACTIVITY_STORAGE_KEY, TOKEN_EXPIRY_STORAGE_KEY } from '@/utils/constants';

export class AuthService {
  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> {
    const response = await apiClient.post<LoginResponse>('/api/v1/auth/login', credentials);

    if (response.success && response.data) {
      // Store tokens and user data
      this.storeAuthData(response.data);
    }

    return response;
  }

  /**
   * Logout user and clear stored data
   */
  async logout(): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post<void>('/api/v1/auth/logout');
      return response;
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
      return {
        success: true,
        meta: {
          timestamp: new Date().toISOString(),
          requestId: 'logout-client',
        },
      };
    } finally {
      // Always clear local data
      this.clearAuthData();
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(): Promise<ApiResponse<{ tokens: { accessToken: string; refreshToken: string; expiresIn: number }; expiresAt: number }>> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!refreshToken) {
      return {
        success: false,
        error: {
          code: 'NO_REFRESH_TOKEN',
          message: 'No refresh token available',
        },
        meta: {
          timestamp: new Date().toISOString(),
          requestId: 'refresh-no-token',
        },
      };
    }

    // Include last activity timestamp for backend inactivity validation
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    const headers: Record<string, string> = {};
    if (lastActivity) {
      headers['X-Last-Activity'] = lastActivity;
    }

    const response = await apiClient.post<{ tokens: { accessToken: string; refreshToken: string; expiresIn: number }; expiresAt: number }>(
      '/api/v1/auth/refresh-token',
      { refreshToken },
      { headers }
    );

    if (response.success && response.data) {
      // Update access token
      localStorage.setItem(TOKEN_STORAGE_KEY, response.data.tokens.accessToken);

      // Update refresh token (backend rotates it for security)
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.data.tokens.refreshToken);

      // Update token expiry time for proactive refresh
      const expiresAt = response.data.expiresAt ?? (Date.now() + (response.data.tokens.expiresIn * 1000));
      localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, expiresAt.toString());
    }

    return response;
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<ApiResponse<User>> {
    return apiClient.get<User>('/api/v1/auth/profile');
  }

  /**
   * Update user profile
   */
  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    const response = await apiClient.put<User>('/api/v1/auth/profile', data);

    if (response.success && response.data) {
      // Update stored user data
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(response.data));
    }

    return response;
  }

  /**
   * Change user password
   */
  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/api/v1/auth/change-password', data);
  }

  /**
   * Enable/disable MFA
   */
  async toggleMFA(enabled: boolean): Promise<ApiResponse<{ qrCode?: string; backupCodes?: string[] }>> {
    return apiClient.post<{ qrCode?: string; backupCodes?: string[] }>('/api/v1/auth/mfa', { enabled });
  }

  /**
   * Verify MFA setup
   */
  async verifyMFA(code: string): Promise<ApiResponse<{ backupCodes: string[] }>> {
    return apiClient.post<{ backupCodes: string[] }>('/api/v1/auth/mfa/verify', { code });
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/api/v1/auth/forgot-password', { email });
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: {
    token: string;
    newPassword: string;
  }): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/api/v1/auth/reset-password', data);
  }

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/api/v1/auth/verify-email', { token });
  }

  /**
   * Resend email verification
   */
  async resendEmailVerification(): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/api/v1/auth/verify-email/resend');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = this.getStoredUser();

    return !!(token && user);
  }

  /**
   * Get stored user data
   */
  getStoredUser(): User | null {
    try {
      const userData = localStorage.getItem(USER_STORAGE_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  }

  /**
   * Check if user has required permission
   */
  hasPermission(permission: string): boolean {
    const user = this.getStoredUser();
    if (!user) {return false;}

    // Super admins have all permissions
    if (user.roles.some(role => role.name === 'SUPER_ADMIN')) {return true;}

    // Check for wildcard permission
    if (user.permissions.includes('*')) {return true;}

    return user.permissions.includes(permission);
  }

  /**
   * Check if user has required role
   */
  hasRole(role: string): boolean {
    const user = this.getStoredUser();
    if (!user) {return false;}

    return user.roles.some(userRole => userRole.name === role);
  }

  /**
   * Check if user has any of the required roles
   */
  hasAnyRole(roles: string[]): boolean {
    const user = this.getStoredUser();
    if (!user) {return false;}

    return roles.some(role => user.roles.some(userRole => userRole.name === role));
  }

  /**
   * Store authentication data in localStorage
   */
  private storeAuthData(data: LoginResponse): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, data.tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.tokens.refreshToken);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));

    // Store token expiry time for proactive refresh
    const expiresAt = Date.now() + (data.tokens.expiresIn * 1000);
    localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, expiresAt.toString());
  }

  /**
   * Clear all authentication data from localStorage
   */
  private clearAuthData(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;